import { Button, ChakraSelect, FormControl, FormLabel, Stack, useDisclosure } from '@elkaso-app/web-design';
import type { DealCategoryTreeNode } from 'apis/exclusive-deals';
import { useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { findCategoryPath } from 'pages/exclusive-deals/components/editable-deals-grid/category-tree-helpers';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

type Option = { value: number; label: string };

type TInputs = {
  l1?: Option | null;
  l2?: Option | null;
};

const toOption = (node: DealCategoryTreeNode): Option => ({ value: node.id, label: node.title });

// Category filter for the families table: pick an L1 (root) category and
// optionally narrow to one of its L2 children. A single catalogCategoryId URL
// param is written (L2 wins over L1) — the BE list endpoint is
// descendant-inclusive, so a grouping id matches every family underneath it.
export const FamilyCategoryFilterButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const { data: treeData } = useGetDealCategoryTreeApi({ enabled: isOpen });
  const tree = useMemo(() => treeData?.data ?? [], [treeData]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<TInputs>({ defaultValues: { l1: null, l2: null } });

  const { catalogCategoryId } = getPageParams();
  const selectedL1 = watch('l1');

  const l1Options = useMemo(() => tree.map(toOption), [tree]);
  const l2Options = useMemo(() => {
    const root = tree.find((node) => node.id === selectedL1?.value);
    return (root?.children ?? []).map(toOption);
  }, [tree, selectedL1]);

  // Hydrate the selects from the URL param: the path from root to the filtered
  // category yields the L1 (path[0]) and, when the filter was an L2, path[1].
  useEffect(() => {
    if (!isOpen) return;
    const id = Number(catalogCategoryId);
    const path = id ? findCategoryPath(tree, id) : [];
    reset({
      l1: path[0] ? toOption(path[0]) : null,
      l2: path[1] ? toOption(path[1]) : null,
    });
  }, [isOpen, catalogCategoryId, tree, reset]);

  const onSubmit = useCallback(
    (data: TInputs) => {
      const newParams = { ...getPageParams() };
      const id = data.l2?.value ?? data.l1?.value;
      if (id) {
        newParams.catalogCategoryId = String(id);
      } else {
        delete newParams.catalogCategoryId;
      }
      setPageParams(newParams);
      onClose();
    },
    [getPageParams, setPageParams, onClose]
  );

  const onClear = useCallback(() => {
    const newParams = { ...getPageParams() };
    delete newParams.catalogCategoryId;
    setPageParams(newParams);
    reset({ l1: null, l2: null });
    onClose();
  }, [getPageParams, setPageParams, reset, onClose]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      isActive={!!catalogCategoryId}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <Controller
            control={control}
            name='l1'
            render={({ field: { onChange, onBlur, value, name, ref } }) => (
              <FormControl>
                <FormLabel>L1 Category</FormLabel>
                <ChakraSelect
                  size='sm'
                  options={l1Options}
                  value={value ?? null}
                  onChange={(option) => {
                    onChange(option ?? null);
                    setValue('l2', null);
                  }}
                  onBlur={onBlur}
                  name={name}
                  ref={ref}
                  isClearable
                  placeholder='All categories'
                />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name='l2'
            render={({ field: { onChange, onBlur, value, name, ref } }) => (
              <FormControl>
                <FormLabel>L2 Category</FormLabel>
                <ChakraSelect
                  size='sm'
                  options={l2Options}
                  value={value ?? null}
                  onChange={(option) => onChange(option ?? null)}
                  onBlur={onBlur}
                  name={name}
                  ref={ref}
                  isClearable
                  isDisabled={!selectedL1}
                  placeholder={selectedL1 ? 'All subcategories' : 'Select an L1 first'}
                />
              </FormControl>
            )}
          />

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
